import React, { useState, useEffect, useCallback } from 'react';
import {
  Alert,
  
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  Tabs,
  Tab,
  Avatar,
  Chip,
  Paper,
  IconButton,
  LinearProgress,
  Stack,
  Divider,
} from '@mui/material';
import {
  Send as SendIcon,
  Inbox as InboxIcon,
  ArrowUpward as ArrowUpwardIcon,
  PersonAdd as FollowIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { styled, useTheme, alpha } from '@mui/material/styles';
import { useUser } from "shared/helpers/axios";
import { useNavigate } from 'react-router-dom';
import { axiosService } from 'shared/helpers/axios';
import LikeButton from 'components/common/buttons/LikeButton';
import FollowButton from 'components/common/buttons/FollowButton';
import useFeedAPI from 'shared/hooks/useFeedAPI';
import PageContainer from 'components/common/layout/PageContainer';
import PageHeader, { HEADER_TABS_SX } from 'components/common/layout/PageHeader';




const SuggestionCard = styled(Card)(({ theme, clickable }) => ({
  // maxWidth: 660,
  marginBottom: theme.spacing(1.5),
  // border: '1px solid #',
  // background: `linear-gradient(135deg, #896b8915 0%, #ffffff 100%)`,
  transition: 'all 0.25s ease',
  cursor: clickable ? 'pointer' : 'default',
  '&:hover': clickable ? {
    transform: 'translateY(-1px)',
    boxShadow: '0 6px 20px #896b8966',
  } : {},
}));

const DiscoverFollowSidebar = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { fetchSuggestionsToFollow } = useFeedAPI();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchSuggestionsToFollow(6);
      setItems(data || []);
    } catch {
      setItems([]);
    } finally {
      setLoading(false);
    }
  }, [fetchSuggestionsToFollow]);

  useEffect(() => { load(); }, [load]);

  const handleItemClick = (s) => {
    if (s.content_type === 'user') navigate(`/profile/${s.object_id}`);
    else if (s.content_type === 'consortium') navigate(`/consortium/${s.object_id}`);
    else if (s.content_type === 'call') navigate(`/funding/call/${s.object_id}`);
    else if (s.content_type === 'organization') navigate(`/organization/${s.object_id}`);
    else if (s.content_type === 'agency') navigate(`/funding/agency/${s.object_id}`);
  };

  return (
    <Card sx={{ 
      // background: 'linear-gradient(135deg, #896b8915 0%, #ffffff 100%)' 
      mt: .5
      }}>
      <CardContent>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <FollowIcon sx={{ fontSize: 18, color: 'primary.main' }} />
            Discover & Follow
          </Typography>
          <IconButton
            size="small"
            onClick={load}
            disabled={loading}
            sx={{
              color: 'primary.main',
              transition: 'transform 0.3s ease',
              '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.08), transform: 'rotate(180deg)' },
            }}
          >
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Box>

        {loading ? (
          <Box py={1}>
            <LinearProgress sx={{ borderRadius: 1 }} />
          </Box>
        ) : items.length === 0 ? (
          <Typography variant="body2" color="text.secondary" textAlign="center" py={2}>
            No suggestions available.
          </Typography>
        ) : (
          <Stack spacing={0}>
            {items.map((s, i) => (
              <Box key={i}>
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ cursor: 'pointer', py: 0.75, borderRadius: 1, px: 0.5, '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.05) } }}
                  onClick={() => handleItemClick(s)}
                >
                  <Box display="flex" alignItems="center" gap={1.25} flex={1} minWidth={0}>
                    <Avatar
                      src={s.avatar}
                      alt={s.name}
                      sx={{ width: 34, height: 34, flexShrink: 0, backgroundColor: theme.palette.primary.avatar, color: theme.palette.primary.contrastText, fontSize: '0.85rem' }}
                    >
                      {s.name?.charAt(0)}
                    </Avatar>
                    <Box flex={1} minWidth={0}>
                      <Typography variant="body2" sx={{ fontWeight: 600, lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {s.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.2 }}>
                        {s.description?.slice(0, 28) || s.content_type}
                      </Typography>
                    </Box>
                  </Box>
                  <Box onClick={(e) => e.stopPropagation()} ml={1} flexShrink={0}>
                    <FollowButton contentType={s.content_type} objectId={s.object_id} size="small" variant="outlined" />
                  </Box>
                </Box>
                {i < items.length - 1 && <Divider sx={{ my: 0.25 }} />}
              </Box>
            ))}
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};

const TabPanel = ({ children, value, index, ...other }) => (
  <div
    role="tabpanel"
    hidden={value !== index}
    id={`suggestion-tabpanel-${index}`}
    aria-labelledby={`suggestion-tab-${index}`}
    {...other}
  >
    {value === index && <Box sx={{ pt: .5 }}>{children}</Box>}
  </div>
);

const TARGET_TYPE_LABELS = {
  call: 'Funding Call',
  fundingcall: 'Funding Call',
  funding_call: 'Funding Call',
  consortium: 'Consortium',
  user: 'Researcher',
  organization: 'Organization',
  proposal: 'Proposal',
  funding_agency: 'Funding Agency',
  funding_program: 'Funding Programme',
};

const getTargetTypeLabel = (type) =>
  TARGET_TYPE_LABELS[(type || '').toLowerCase()] || (type ? type.charAt(0).toUpperCase() + type.slice(1) : 'Entity');

const getEntityPath = (type, id) => {
  if (!type || !id) return null;
  switch (type.toLowerCase()) {
    case 'user':          return `/profile/${id}`;
    case 'organization':  return `/organization/${id}`;
    case 'consortium':    return `/consortium/${id}`;
    case 'call':
    case 'fundingcall':
    case 'funding_call':  return `/funding/call/${id}`;
    case 'fundingagency':
    case 'funding_agency':
    case 'fundingprogram':
    case 'funding_program': return `/funding/`;
    default:              return null;
  }
};

const SuggestionItem = ({ suggestion, currentUser }) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'short', day: 'numeric',
    });
  };

  const userId = currentUser?.id;
  const isReceived = suggestion.recipient === userId;
  const person = isReceived ? suggestion.author_info : suggestion.recipient_info;
  const personRole = isReceived ? 'From' : 'To';
  const entityPath = getEntityPath(suggestion.target_type, suggestion.target_id);

  const handleClick = () => {
    if (entityPath) navigate(entityPath);
  };

  return (
    <SuggestionCard clickable={entityPath ? 1 : 0} onClick={handleClick}>
      <CardContent sx={{ p: 2.5, '&:last-child': { pb: 1.5 } }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Box display="flex" alignItems="center" gap={1.5}>
            <Avatar
              src={person?.avatar}
              sx={{
                width: 34, height: 34,
                backgroundColor: theme.palette.primary.avatar2,
                color: theme.palette.primary.contrastText,
                fontSize: '0.85rem',
              }}
            >
              {person?.name?.charAt(0) || 'U'}
            </Avatar>
            <Box>
              <Typography variant="subtitle2" sx={{ fontWeight: 600, lineHeight: 1.2 }}>
                {getTargetTypeLabel(suggestion.target_type)}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {suggestion.kind_display || suggestion.kind} · {personRole}: {person?.name || 'Unknown'}
              </Typography>
            </Box>
          </Box>
          <Chip
            label={suggestion.status_display || suggestion.status}
            color={suggestion.status === 'accepted' ? 'success' : suggestion.status === 'rejected' ? 'error' : 'default'}
            size="small"
            sx={{ height: 22, fontSize: '0.7rem' }}
          />
        </Box>

        <Typography variant="body2" sx={{ mb: suggestion.target_name ? 1 : 1.5, lineHeight: 1.55, color: 'text.primary' }}>
          {suggestion.body}
        </Typography>

        {suggestion.target_name && (
          <Paper elevation={0} sx={{ px: 1.5, py: 0.75, mb: 1, backgroundColor: '#ad89ad1a', borderRadius: 1 }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: 'primary.main', display: 'block' }}>
              {getTargetTypeLabel(suggestion.target_type)}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {suggestion.target_name}
            </Typography>
          </Paper>
        )}

        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="caption" color="text.secondary">
            {isReceived ? 'Received' : 'Sent'}: {formatDate(suggestion.created)}
          </Typography>
          <Box onClick={(e) => e.stopPropagation()} >
            <LikeButton
              contentType="suggestion"
              contentId={suggestion.id}
              initialLikeCount={suggestion.like_count || 0}
              initialIsLiked={suggestion.is_liked || false}
              size="small"
            />
          </Box>
        </Box>
      </CardContent>
    </SuggestionCard>
  );
};

const ModernSuggestionsPage = () => {
  const [currentTab, setCurrentTab] = useState(0);
  const [suggestions, setSuggestions] = useState([]);
  const [filter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const navigate = useNavigate();
  const { user } = useUser();

  useEffect(() => {
    fetchSuggestions();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentTab]);

  const fetchSuggestions = async () => {
    setLoading(true);
    setError(null);
    try {
      // Use the centralized axios instance - it automatically adds auth headers
      const response = await axiosService.get('/interact/suggestions/');
      
      const suggestionsData = response.data.results || response.data;
      setSuggestions(suggestionsData);
      
    } catch (error) {
      console.error('Error fetching suggestions:', error);
      setError('Failed to load suggestions. Please try again.');
      setSuggestions([]);
      
      // If it's an auth error, redirect to login
      if (error.response?.status === 401) {
        navigate('/login/');
      }
    } finally {
      setLoading(false);
    }
  };

  const filteredSuggestions = suggestions.filter(suggestion => {
    const userId = user?.id;
    
    if (!userId) {
      return false;
    }
    
    console.log("### userId:", userId);
    console.log ("### recipient:", suggestion.recipient);

    const isReceived = suggestion.recipient  === userId;
    const isSent = suggestion.author === userId;

    console.log("### isReceived:", isReceived, "isSent:", isSent);

    if (currentTab === 0 && !isReceived) return false;
    if (currentTab === 1 && !isSent) return false;

    if (filter === 'all') return true;
    return suggestion.status === filter;
  });

  return (
    <PageContainer>
      {/* Header */}
      <PageHeader title="Collaboration Suggestions"
        subtitle="Manage collaboration requests, funding opportunities, and research suggestions">
        <Tabs value={currentTab} onChange={(e, v) => setCurrentTab(v)} variant="scrollable" scrollButtons="auto"
          sx={{ ...HEADER_TABS_SX, minWidth: 0 }}>
          <Tab label="Received" icon={<InboxIcon />} iconPosition="start" />
          <Tab label="Sent" icon={<ArrowUpwardIcon />} iconPosition="start" />
          <Tab label="All Suggestions" icon={<SendIcon />} iconPosition="start" />
        </Tabs>
      </PageHeader>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filter Chips */}
      {/* <Box mb={3}>
        <Stack direction="row" spacing={1} flexWrap="wrap">
          {['all', 'open', 'accepted', 'rejected'].map((status) => (
            <Chip
              key={status}
              label={status.charAt(0).toUpperCase() + status.slice(1)}
              onClick={() => setFilter(status)}
              variant={filter === status ? 'filled' : 'outlined'}
              color={filter === status ? 'primary' : 'default'}
              sx={{ cursor: 'pointer', mb: 1 }}
            />
          ))}
        </Stack>
      </Box> */}

      {/* Two-column layout: suggestions left, sidebar right */}
      <Grid container spacing={3} sx={{ mt: -2 }} alignItems="flex-start">   {/* 8px under the header, not the spacing's 24 */}
        {/* Left: tab panels */}
        {/* <Grid item xs={12} md={.3}></Grid> */}
        <Grid item xs={12} md={8}>
          <TabPanel value={currentTab} index={0}>
            {loading ? (
              <Card><CardContent sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1">Loading suggestions...</Typography>
              </CardContent></Card>
            ) : filteredSuggestions.length === 0 ? (
              <Card><CardContent sx={{ textAlign: 'center', py: 8 }}>
                <InboxIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>No suggestions found</Typography>
                <Typography variant="body2" color="text.secondary">New collaboration requests will appear here</Typography>
              </CardContent></Card>
            ) : (
              filteredSuggestions.map(suggestion => (
                <SuggestionItem key={suggestion.id} suggestion={suggestion} currentUser={user} />
              ))
            )}
          </TabPanel>

          <TabPanel value={currentTab} index={1}>
            {loading ? (
              <Card><CardContent sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1">Loading sent suggestions...</Typography>
              </CardContent></Card>
            ) : filteredSuggestions.length === 0 ? (
              <Card><CardContent sx={{ textAlign: 'center', py: 8 }}>
                <ArrowUpwardIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>No sent suggestions</Typography>
                <Typography variant="body2" color="text.secondary">Suggestions you send to other researchers will appear here</Typography>
              </CardContent></Card>
            ) : (
              filteredSuggestions.map(suggestion => (
                <SuggestionItem key={suggestion.id} suggestion={suggestion} currentUser={user} />
              ))
            )}
          </TabPanel>

          <TabPanel value={currentTab} index={2}>
            {loading ? (
              <Card><CardContent sx={{ textAlign: 'center', py: 4 }}>
                <Typography variant="body1">Loading all suggestions...</Typography>
              </CardContent></Card>
            ) : suggestions.length === 0 ? (
              <Card><CardContent sx={{ textAlign: 'center', py: 8 }}>
                <SendIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
                <Typography variant="h6" color="text.secondary" gutterBottom>No suggestions yet</Typography>
                <Typography variant="body2" color="text.secondary">Start collaborating by sending or receiving research suggestions</Typography>
              </CardContent></Card>
            ) : (
              suggestions.map(suggestion => (
                <SuggestionItem key={suggestion.id} suggestion={suggestion} currentUser={user} />
              ))
            )}
          </TabPanel>
        </Grid>

        {/* Right: sidebar */}
        <Grid item xs={12} md={3.5}>
        
            <DiscoverFollowSidebar />
          
        </Grid>
      </Grid>
    </PageContainer>
  );
};

export default ModernSuggestionsPage;